import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../../components/ui/table';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../../../components/ui/dropdown-menu';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { useToast } from '../../../components/ui/use-toast';
import { campaignService } from '../../../services/crowdfunding';
import { CrowdfundingCampaign } from '../../../types/crowdfunding';
import { formatCurrency } from '../../../utils/formatters';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Search, 
  Plus, 
  MoreVertical, 
  Edit, 
  Trash, 
  Eye, 
  Check, 
  X, 
  Award, 
  RotateCw, 
  Copy 
} from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../../lib/supabase';

export const CampaignList: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'active' | 'completed' | 'cancelled'>('all');
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['admin-campaigns', statusFilter],
    queryFn: async () => {
      const status = statusFilter === 'all' ? undefined : statusFilter;
      const tenantId = import.meta.env.VITE_TENANT_ID || '2';
      return await campaignService.getCampaigns({ 
        status: status as any,
        tenantId
      });
    }
  });
  const campaigns = data?.campaigns ?? [];
  
  // Filtrer les campagnes en fonction du terme de recherche
  const filteredCampaigns = campaigns.filter(campaign => 
    campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.contestant?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Changer le statut d'une campagne
  const handleStatusChange = async (campaignId: string, newStatus: 'draft' | 'active' | 'completed' | 'cancelled') => {
    try {
      await campaignService.updateCampaignStatus(campaignId, newStatus);
      
      toast({
        title: "Statut mis à jour",
        description: "Le statut de la campagne a été mis à jour avec succès.",
        variant: "default"
      });
      
      // Actualiser les données
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la mise à jour du statut.",
        variant: "destructive"
      });
    }
  };
  
  // Supprimer une campagne
  const handleDeleteCampaign = async (campaignId: string) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette campagne ? Cette action est irréversible.")) {
      return;
    }
    
    try {
      await campaignService.deleteCampaign(campaignId);
      
      toast({
        title: "Campagne supprimée",
        description: "La campagne a été supprimée avec succès.",
        variant: "default"
      });
      
      // Actualiser les données
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
      
    } catch (error) {
      console.error('Erreur lors de la suppression de la campagne:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la suppression de la campagne.",
        variant: "destructive"
      });
    }
  };
  
  const handleReactivateCampaign = async (campaignId: string) => {
    try {
      await campaignService.updateCampaignStatus(campaignId, 'active');
      toast({
        title: "Campagne réactivée",
        description: "La campagne a été remise en statut actif.",
        variant: "default"
      });
      queryClient.invalidateQueries({ queryKey: ['admin-campaigns'] });
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Une erreur est survenue lors de la réactivation.",
        variant: "destructive"
      });
    }
  };

  // Fonction pour dupliquer une campagne
  const handleDuplicate = async (id: string) => {
    try {
      // Récupérer la campagne originale
      const { data: original, error } = await supabase
        .from('crowdfunding_campaigns')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      if (!original) return;

      // Créer une copie avec nouvel ID et nouveau slug
      const duplicate = { 
        ...original, 
        id: uuidv4(),
        slug: `${original.slug}-copie-${Date.now()}`,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Insérer la copie dans la base de données
      const { error: insertError } = await supabase
        .from('crowdfunding_campaigns')
        .insert([duplicate]);

      if (insertError) throw insertError;

      toast({
        title: 'Succès',
        description: 'Campagne dupliquée avec succès',
      });

      // Rafraîchir les données
      refetch();
    } catch (error) {
      console.error('Erreur lors de la duplication:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de dupliquer la campagne',
        variant: 'destructive',
      });
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-100">Gestion des Campagnes</h1>
            <p className="text-gray-300">
              Gérez les campagnes de financement participatif
            </p>
          </div>
          
          <Link to="/admin/crowdfunding/create">
            <Button className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-medium transition-all duration-300 transform hover:scale-105">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle campagne
            </Button>
          </Link>
        </div>
        
        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6">
          <Tabs 
            value={statusFilter} 
            onValueChange={(value) => setStatusFilter(value as any)}
            className="w-full md:w-auto"
          >
            <TabsList className="bg-white/5 border border-white/10 text-gray-100">
              <TabsTrigger value="all">Toutes</TabsTrigger>
              <TabsTrigger value="draft">Brouillons</TabsTrigger>
              <TabsTrigger value="active">Actives</TabsTrigger>
              <TabsTrigger value="completed">Terminées</TabsTrigger>
              <TabsTrigger value="cancelled">Annulées</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <Input
              type="text"
              placeholder="Rechercher une campagne..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-gray-100 placeholder-gray-400"
            />
          </div>
        </div>
        
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
            </div>
          ) : filteredCampaigns.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-gray-100">Titre</TableHead>
                    <TableHead className="text-gray-100">Candidat</TableHead>
                    <TableHead className="text-gray-100">Objectif</TableHead>
                    <TableHead className="text-gray-100">Collecté</TableHead>
                    <TableHead className="text-gray-100">Progression</TableHead>
                    <TableHead className="text-gray-100">Statut</TableHead>
                    <TableHead className="text-gray-100">Date de fin</TableHead>
                    <TableHead className="text-gray-100 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCampaigns.map((campaign) => {
                    const progress = campaign.goal_amount > 0 
                      ? Math.min(Math.round((campaign.collected_amount / campaign.goal_amount) * 100), 100)
                      : 0;
                    
                    return (
                      <TableRow key={campaign.id}>
                        <TableCell className="font-medium text-gray-50">
                          <Link 
                            to={`/admin/crowdfunding/edit/${campaign.slug}`}
                            className="text-yellow-400 hover:text-yellow-300 hover:underline"
                          >
                            {campaign.title}
                          </Link>
                        </TableCell>
                        <TableCell>
                          {campaign.contestant ? (
                            <Link 
                              to={`/admin/contestants?search=${campaign.contestant.name}`}
                              className="text-yellow-300 hover:underline"
                            >
                              {campaign.contestant.name}
                            </Link>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-gray-200">{formatCurrency(campaign.goal_amount)} FCFA</TableCell>
                        <TableCell className="text-gray-200">{formatCurrency(campaign.collected_amount)} FCFA</TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <div className="w-full bg-gray-700 rounded-full h-2 mr-2">
                              <div 
                                className="bg-yellow-400 h-2 rounded-full" 
                                style={{ width: `${progress}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-200">{progress}%</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 rounded-full text-xs ${
                            campaign.status === 'active' 
                              ? 'bg-green-500/20 text-green-400' 
                              : campaign.status === 'completed' 
                                ? 'bg-blue-500/20 text-blue-400' 
                                : campaign.status === 'draft'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : 'bg-red-500/20 text-red-400'
                          }`}>
                            {campaign.status === 'active' 
                              ? 'Active' 
                              : campaign.status === 'completed' 
                                ? 'Terminée' 
                                : campaign.status === 'draft'
                                  ? 'Brouillon'
                                  : 'Annulée'}
                          </span>
                        </TableCell>
                        <TableCell className="text-gray-200">
                          {format(new Date(campaign.end_date), 'dd MMM yyyy', { locale: fr })}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-8 w-8 p-0 text-gray-300 hover:text-white">
                                <span className="sr-only">Ouvrir le menu</span>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-black/90 border-white/10">
                              <DropdownMenuItem 
                                className="text-gray-200 hover:text-white focus:text-white cursor-pointer"
                                onClick={() => window.open(`/crowdfunding/${campaign.slug}`, '_blank')}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                Voir
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                className="text-gray-200 hover:text-white focus:text-white cursor-pointer"
                                onClick={() => navigate(`/admin/crowdfunding/edit/${campaign.slug}`)}
                              >
                                <Edit className="mr-2 h-4 w-4" />
                                Modifier
                              </DropdownMenuItem>
                              
                              {campaign.status === 'draft' && (
                                <DropdownMenuItem 
                                  className="text-green-400 hover:text-green-300 focus:text-green-300 cursor-pointer"
                                  onClick={() => handleStatusChange(campaign.id, 'active')}
                                >
                                  <Check className="mr-2 h-4 w-4" />
                                  Activer
                                </DropdownMenuItem>
                              )}
                              
                              {campaign.status === 'active' && (
                                <>
                                  <DropdownMenuItem 
                                    className="text-blue-400 hover:text-blue-300 focus:text-blue-300 cursor-pointer"
                                    onClick={() => handleStatusChange(campaign.id, 'completed')}
                                  >
                                    <Award className="mr-2 h-4 w-4" />
                                    Terminer
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    className="text-red-400 hover:text-red-300 focus:text-red-300 cursor-pointer"
                                    onClick={() => handleStatusChange(campaign.id, 'cancelled')}
                                  >
                                    <X className="mr-2 h-4 w-4" />
                                    Annuler
                                  </DropdownMenuItem>
                                </>
                              )}
                              
                              {(campaign.status === 'completed' || campaign.status === 'cancelled') && (
                                <DropdownMenuItem 
                                  className="text-yellow-500 hover:text-yellow-300 focus:text-yellow-300 cursor-pointer"
                                  onClick={() => handleReactivateCampaign(campaign.id)}
                                >
                                  <RotateCw className="mr-2 h-4 w-4" />
                                  Réactiver
                                </DropdownMenuItem>
                              )}
                              
                              <DropdownMenuItem 
                                className="text-green-400 hover:text-green-300 focus:text-green-300 cursor-pointer"
                                onClick={() => handleDuplicate(campaign.id)}
                              >
                                <Copy className="mr-2 h-4 w-4" />
                                Dupliquer
                              </DropdownMenuItem>
                              
                              <DropdownMenuItem 
                                className="text-red-500 hover:text-red-300 focus:text-red-300 cursor-pointer"
                                onClick={() => handleDeleteCampaign(campaign.id)}
                              >
                                <Trash className="mr-2 h-4 w-4" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-16">
              <Award className="h-16 w-16 text-yellow-400/50 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-2">Aucune campagne trouvée</h3>
              <p className="text-gray-400 max-w-md mx-auto mb-6">
                {searchTerm 
                  ? `Aucune campagne ne correspond à votre recherche "${searchTerm}".` 
                  : "Aucune campagne n'est disponible pour le moment."}
              </p>
              <Link to="/admin/crowdfunding/create">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Créer une campagne
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};
